import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Stack, Button, Group, Text } from '@repletoryreach/sdk-ui';
import {
  Alert,
  EmptyState,
  KpiFilterRow,
  PageHeader,
  TableShell,
  type KpiFilterItem,
  type ViewMode
} from '@repletoryreach/sdk-ui/data-display';
import { AUDIT_SOURCES, useAuditFeed, type AuditRow, type AuditSource } from '../../data/useAuditFeed';
import { useTenantMap } from '../../data/useTenants';
import { RangePicker, rangeToSinceIso, type RangeOption } from './components/RangePicker';
import { TimelineRow } from './components/TimelineRow';
import { AuditTable } from './components/AuditTable';
import { ExportCsvButton } from './components/ExportCsvButton';
import { AuditDetailDrawer } from './components/AuditDetailDrawer';
import { SOURCE_LABEL_KEY } from './components/sourceColors';
import { FilterDrawer, type FilterField, type FilterValues } from '@repletoryreach/sdk-ui/data-display';
import { TableFooter } from '../../components/TableFooter';
import { ActiveFilterPills, type ActiveFilterPill } from '../../components/ActiveFilterPills';
import { usePersistedState } from '../../lib/usePersistedState';
import { formatDate, formatInteger, formatRelative } from '../../lib/format';
import { useT } from '../../i18n/I18nProvider';

type KpiKey = 'all' | AuditSource;

const ROW_BUDGET = 500;
const MUTED = 'var(--rr-color-text-muted)';

const FeedIcon = (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M4.5 5.5h15M4.5 12h15M4.5 18.5h9" />
  </svg>
);
const SparkIcon = (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M12 3.5l2.2 5.3 5.3 2.2-5.3 2.2L12 18.5l-2.2-5.3L4.5 11l5.3-2.2z" />
  </svg>
);
const CoinIcon = (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <ellipse cx="12" cy="6.5" rx="7.5" ry="3" /><path d="M4.5 6.5v11c0 1.7 3.4 3 7.5 3s7.5-1.3 7.5-3v-11" /><path d="M4.5 12c0 1.7 3.4 3 7.5 3s7.5-1.3 7.5-3" />
  </svg>
);
const TransferIcon = (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M4 8.5h13M13.5 5l3.5 3.5L13.5 12" /><path d="M20 15.5H7M10.5 12L7 15.5 10.5 19" />
  </svg>
);
const WebhookIcon = (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <circle cx="8" cy="7" r="3" /><circle cx="17" cy="16" r="3" /><circle cx="6.5" cy="17" r="3" /><path d="M9.8 9.4l4.2 4.5M8.5 17h5" />
  </svg>
);
const CheckIcon = (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <circle cx="12" cy="12" r="8.5" /><path d="M8.5 12.2l2.4 2.4 4.6-5" />
  </svg>
);

const ImpersonateIcon = (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M2.5 12S6 6 12 6s9.5 6 9.5 6-3.5 6-9.5 6-9.5-6-9.5-6z" /><circle cx="12" cy="12" r="2.6" />
  </svg>
);

export default function AuditPage() {
  const t = useT();
  const [range, setRange] = useState<RangeOption>('7d');
  const since = useMemo(() => rangeToSinceIso(range), [range]);

  // The audit feed is a fan-in across 6 endpoints. We fetch ALL sources, then
  // filter client-side from KPI/drawer state.
  const allEnabled = useMemo(
    () => Object.fromEntries(AUDIT_SOURCES.map((s) => [s, true])) as Record<AuditSource, boolean>,
    []
  );
  const {
    rows: allRows, loading, fetching, failedSources, denied, capped, sourceTotals, updatedAt, refetch
  } = useAuditFeed(since, allEnabled, ROW_BUDGET);
  const { map: tenants } = useTenantMap();

  const [view, setView] = usePersistedState<ViewMode>('admin.audit.view', 'list');
  const [pageSize, setPageSize] = usePersistedState<number>('admin.audit.pageSize', 50);

  const [kpi, setKpi] = useState<KpiKey | null>('all');
  // Which event the detail panel is showing, by its position in the visible
  // page, so previous and next can walk the list the operator is looking at.
  const [selected, setSelected] = useState<number | null>(null);
  const [filters, setFilters] = useState<FilterValues>({});
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(1);

  const counts = useMemo(() => {
    const base: Record<KpiKey, number> = {
      all: allRows.length,
      transfers: 0, 'opt-in': 0, deliveries: 0, 'flow-runs': 0, ledger: 0, impersonations: 0
    };
    for (const r of allRows) base[r.source]++;
    return base;
  }, [allRows]);

  // How many of the five feeds answered. It rides on the "All" chip, because
  // five chips reading zero and one line saying why is the difference between
  // a quiet week and a broken query.
  const sourcesOk = AUDIT_SOURCES.length - failedSources.length;
  const allFailed = failedSources.length === AUDIT_SOURCES.length;

  /*
   * One chip per source. A source whose query failed shows "n/a" and a warning
   * tone rather than a zero: a chip reading 0 next to a red banner is how an
   * audit log tells an operator that nothing happened when the truth is that
   * nobody asked. The hint carries the share of the window, or the true total
   * where the endpoint reports one.
   */
  const sourceChip = (id: AuditSource, label: string, icon: ReactNode): KpiFilterItem => {
    const failed = failedSources.includes(id);
    const count = counts[id];
    const total = sourceTotals[id];
    return {
      id,
      label,
      icon,
      value: failed ? t('common.notAvailable') : formatInteger(count),
      tone: failed ? 'down' : 'neutral',
      hint: failed
        ? t('audit.kpi.unavailable')
        : total !== undefined && total > count
          ? t('audit.kpi.windowTotal', { total: formatInteger(total) })
          : counts.all > 0
            ? t('audit.kpi.share', { percent: Math.round((count / counts.all) * 100) })
            : undefined
    };
  };

  const kpiItems: KpiFilterItem[] = [
    {
      id: 'all',
      label: t('audit.kpi.all'),
      icon: FeedIcon,
      value: allFailed ? t('common.notAvailable') : formatInteger(counts.all),
      tone: 'primary',
      hint: t('audit.stats.sourcesHint', { ok: sourcesOk, total: AUDIT_SOURCES.length })
    },
    // Impersonations lead the source chips: it is the only feed here that
    // names a person, and the highest-risk act the platform records.
    sourceChip('impersonations', t('audit.kpi.impersonations'), ImpersonateIcon),
    sourceChip('ledger', t('audit.kpi.ledger'), CoinIcon),
    sourceChip('transfers', t('audit.kpi.transfers'), TransferIcon),
    sourceChip('deliveries', t('audit.kpi.deliveries'), WebhookIcon),
    sourceChip('flow-runs', t('audit.kpi.flowRuns'), SparkIcon),
    sourceChip('opt-in', t('audit.kpi.optIn'), CheckIcon)
  ];

  const filterFields: FilterField[] = useMemo(() => {
    // The organisation options carry the tenant's name. A dropdown of GUID
    // prefixes asks the operator to do the join the console should do.
    const orgs = Array.from(new Set(allRows.map((r) => r.organisation_id)))
      .map((id) => ({ value: id, label: tenants.get(id)?.name ?? t('tenantName.unknownShort') }))
      .sort((a, b) => a.label.localeCompare(b.label));
    return [
      {
        key: 'source',
        label: t('audit.columns.source'),
        placeholder: t('audit.filters.anySource'),
        options: AUDIT_SOURCES.map((s) => ({ value: s, label: t(SOURCE_LABEL_KEY[s]) }))
      },
      {
        key: 'organisation',
        label: t('billing.subscriptions.filters.organisation'),
        placeholder: t('users.filters.anyOrganisation'),
        options: orgs
      }
    ];
  }, [allRows, tenants, t]);

  const drawerActive = Object.values(filters).filter((v) => v && v !== 'all').length;
  const hasActiveFilters =
    (kpi !== null && kpi !== 'all') || drawerActive > 0 || range !== '7d' || Boolean(query.trim());

  function clearAllFilters() {
    setKpi('all'); setFilters({}); setRange('7d'); setQuery(''); setPage(1);
  }

  const filtered = useMemo(() => {
    let rows = allRows;
    if (kpi && kpi !== 'all') rows = rows.filter((r) => r.source === kpi);
    if (filters.source) rows = rows.filter((r) => r.source === filters.source);
    if (filters.organisation) rows = rows.filter((r) => r.organisation_id === filters.organisation);
    const q = query.trim().toLowerCase();
    if (q) {
      rows = rows.filter((r) => {
        const tenant = tenants.get(r.organisation_id);
        return `${r.summary} ${r.detail ?? ''} ${tenant?.name ?? ''} ${tenant?.slug ?? ''}`.toLowerCase().includes(q);
      });
    }
    return rows;
  }, [allRows, kpi, filters, query, tenants]);

  useEffect(() => {
    if (page > 1 && (page - 1) * pageSize >= filtered.length) setPage(1);
  }, [filtered.length, page, pageSize]);

  const paged = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filtered.slice(start, start + pageSize);
  }, [filtered, page, pageSize]);

  const selectedRow: AuditRow | null = selected !== null ? paged[selected] ?? null : null;

  // Day headings turn a flat wall of rows into something scannable. The rows
  // are already newest-first, so a heading is emitted whenever the date moves.
  const grouped = useMemo(() => {
    const out: { day: string; rows: typeof paged }[] = [];
    for (const row of paged) {
      const day = formatDate(row.occurred_at);
      const last = out[out.length - 1];
      if (last && last.day === day) last.rows.push(row);
      else out.push({ day, rows: [row] });
    }
    return out;
  }, [paged]);

  const failedLabel = failedSources.map((s) => t(SOURCE_LABEL_KEY[s])).join(', ');

  const pills: ActiveFilterPill[] = [];
  if (query.trim()) pills.push({ id: 'query', field: t('common.search'), value: query.trim() });
  if (range !== '7d') pills.push({ id: 'range', field: t('audit.range.label'), value: t(`audit.range.${range}`) });
  if (kpi && kpi !== 'all') {
    pills.push({ id: 'kpi', field: t('audit.columns.source'), value: t(SOURCE_LABEL_KEY[kpi]) });
  }
  if (filters.source) {
    pills.push({ id: 'source', field: t('audit.columns.source'), value: t(SOURCE_LABEL_KEY[filters.source as AuditSource]) });
  }
  if (filters.organisation) {
    pills.push({
      id: 'organisation',
      field: t('billing.subscriptions.filters.organisation'),
      value: tenants.get(filters.organisation)?.name ?? t('tenantName.unknownShort')
    });
  }

  function removePill(id: string) {
    setPage(1);
    if (id === 'query') { setQuery(''); return; }
    if (id === 'range') { setRange('7d'); return; }
    if (id === 'kpi') { setKpi('all'); return; }
    setFilters((prev) => { const next = { ...prev }; delete next[id]; return next; });
  }

  return (
    <Stack gap="lg">
      <PageHeader
        title={t('audit.title')}
        description={t('audit.description')}
        actions={
          <Group gap="sm" wrap="wrap" align="center">
            {updatedAt > 0 && (
              <Text role="caption" c={MUTED}>
                {t('audit.lastUpdated', { when: formatRelative(new Date(updatedAt).toISOString()) })}
              </Text>
            )}
            <Button size="sm" variant="default" onClick={refetch} loading={fetching}>
              {t('action.refresh')}
            </Button>
            {/* Exporting the window is what this page is opened to do during an
                incident review, so it is the page's primary action. */}
            <ExportCsvButton rows={filtered} />
          </Group>
        }
      />

      {denied && (
        <Alert color="warning" title={t('audit.denied.title')}>
          {t('audit.denied.body')}
        </Alert>
      )}

      {!denied && failedSources.length > 0 && (
        <Alert color="error" title={t('audit.partialError.title')}>
          <Group justify="space-between" align="center" style={{ width: '100%' }}>
            <span>{t('audit.partialError.body', { sources: failedLabel })}</span>
            <Button size="xs" variant="outline" onClick={refetch}>{t('action.retry')}</Button>
          </Group>
        </Alert>
      )}

      <TableShell
        title={t('audit.tableTitle')}
        totalLabel={
          capped
            ? t('audit.showingEventsCapped', { count: formatInteger(filtered.length) })
            : t('audit.showingEvents', { count: formatInteger(filtered.length) })
        }
        kpis={
          <Stack gap="xs">
            <Text role="overline" c={MUTED}>{t('audit.sourcesLegend')}</Text>
            <KpiFilterRow
              items={kpiItems}
              value={kpi}
              onChange={(next) => { setKpi((next ?? 'all') as KpiKey); setPage(1); }}
              filteringLabel={t('filters.filtering')}
            />
          </Stack>
        }
        search={query}
        onSearch={(v) => { setQuery(v); setPage(1); }}
        searchPlaceholder={t('audit.searchPlaceholder')}
        filters={
          <>
            {/* The window is a control of the list, so it sits in the toolbar
                beside the search that narrows the same list. */}
            <RangePicker value={range} onChange={(r) => { setRange(r); setPage(1); }} />
            <FilterDrawer fields={filterFields} values={filters} onApply={(next) => { setFilters(next); setPage(1); }} />
          </>
        }
        belowToolbar={<ActiveFilterPills pills={pills} onRemove={removePill} onClear={clearAllFilters} />}
        view={view}
        onViewChange={setView}
        availableViews={['table', 'list']}
        loading={loading}
        isEmpty={!loading && filtered.length === 0}
        empty={
          <EmptyState
            title={t('audit.empty')}
            description={hasActiveFilters ? t('audit.emptyHint') : t('audit.emptyUnfiltered')}
            action={hasActiveFilters ? <Button size="xs" variant="outline" onClick={clearAllFilters}>{t('common.clearFilters')}</Button> : undefined}
          />
        }
        pagination={
          <TableFooter
            page={page}
            pageSize={pageSize}
            total={filtered.length}
            capped={capped}
            onPageChange={setPage}
            onPageSizeChange={(n) => { setPageSize(n); setPage(1); }}
          />
        }
      >
        <Stack gap="md">
          {view === 'list' && (
            <ul className="rr-audit-timeline" aria-label={t('audit.tableTitle')}>
              {grouped.map((group) => (
                <li key={group.day} style={{ listStyle: 'none' }}>
                  <div className="rr-audit-day">{group.day}</div>
                  <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
                    {group.rows.map((row) => (
                      <TimelineRow
                        key={`${row.source}-${row.id}`}
                        row={row}
                        onOpen={() => setSelected(paged.indexOf(row))}
                      />
                    ))}
                  </ul>
                </li>
              ))}
            </ul>
          )}
          {view === 'table' && (
            <AuditTable rows={paged} loading={loading} onOpen={(row) => setSelected(paged.indexOf(row))} />
          )}
        </Stack>
      </TableShell>

      <AuditDetailDrawer
        row={selectedRow}
        onClose={() => setSelected(null)}
        onPrevious={selected !== null && selected > 0 ? () => setSelected(selected - 1) : undefined}
        onNext={selected !== null && selected < paged.length - 1 ? () => setSelected(selected + 1) : undefined}
      />
    </Stack>
  );
}
